// A question asked on the new-question screen (AskHero), before and after it becomes a
// forecast. Pure; hooks/useAskHeroSubmit.js holds the state.

import { DRAFT_CONV_ID, latestAssistantReplySince } from "./draft.js";
import { STATUS } from "./model.js";

/**
 * The control a question asked on the new-question screen came from (`chat.ask` origin): the
 * example the user picked when it is asked unchanged, else the box's Ask.
 * @param {string} question what was asked (trimmed)
 * @param {string|null} picked the example last put in the box, if any
 */
export function askHeroOrigin(question, picked) {
  return picked && question === picked ? "ask-hero.example" : "ask-hero.submit";
}

/** The stand-in run's id: a placeholder, never a forecast's. */
export const ASK_HERO_PENDING_RUN_ID = "ask-hero-pending";

/**
 * The progress card's stand-in run while a just-asked question has no run yet: the question on
 * its first step ("Checking your question"), timed from when it was asked.
 * @param {{question: string, startedAt: string}|null} pending
 * @param {string|null} engine
 */
export function askHeroPendingRun(pending, engine) {
  if (!pending) return null;
  return {
    runId: ASK_HERO_PENDING_RUN_ID,
    status: STATUS.PREPROCESSING,
    question: pending.question,
    createdAt: pending.startedAt,
    backend: engine || null,
    progress: null,
    contract: null,
  };
}

/**
 * Send a question and wait until its chat turn has ended. The chat handler starts a submission or
 * a pre-forecast answer (which may itself hand off to a submission) without waiting for it; the
 * turn it opens ends only once that result, a rejection included, is on screen.
 * @param {() => unknown} send starts the chat handler for the question
 * @param {() => {ended: () => Promise<void>}|null} currentTurn the open chat turn, if any
 */
export async function sendAndAwaitTurn(send, currentTurn) {
  const before = currentTurn();
  const started = send();
  // The handler opens its turn before its first await, so this is the question's own turn; an
  // early return opens none.
  const turn = currentTurn();
  await started;
  if (turn && turn !== before) await turn.ended();
}

/**
 * Why a question asked at `sinceMs` did not become a forecast, once its chat turn is over: Sayer's
 * reply in the draft chat (a forecast's own replies go to its conversation), with the question to
 * put back in the box; null when Sayer said nothing there. Shown only on the new-question screen,
 * which no forecast is open over.
 * @param {object} state the run store's state
 * @param {number} sinceMs
 * @param {string} question
 * @returns {{text: string, question: string}|null}
 */
export function askHeroNotice(state, sinceMs, question) {
  const reply = latestAssistantReplySince(
    state?.conversationsById?.[DRAFT_CONV_ID]?.entries,
    sinceMs,
  );
  return reply ? { text: reply, question } : null;
}
